import * as Status from '../../../constants/status';
import * as Role from '../../../constants/role';
import moment from 'moment';
import { isRoleAuditoriaNsir } from '../../../helpers/auditoriaNsirPermission';
import { toDigitacaoAntecipadaApiFlag } from '../../../helpers/digitacaoAntecipada';
import { getNsirFiltroFixoAuditoriaNsir, INSPECAO_USUO_MUTUO_ID, mergeFiltroNsirComRestricoesAuditoria } from '../../../utils';

export const DEFAULT_DATE_TYPES = [
  'DATA_CRIACAO',
  'DATA_DIGITACAO',
  'DATA_EXECUTADA_GDIS',
  'DATA_INSPECAO',
  'DATA_VENCIMENTO',
];

export const DEFAULT_STATUS = [
  Status.AGUARDANDO_INSPECAO,
  Status.EM_INSPECAO,
  Status.INSPECAO_EM_ENVIO,
  Status.INSPECAO_FINALIZADA,
  Status.INSPECAO_ENVIADA_PARCIAL,
  Status.AGUARDANDO_DIGITACAO,
  Status.EM_DIGITACAO,
  Status.DIGITACAO_FINALIZADA,
];

export const formatPeriodLabel = (inicio, fim) => {
  if (inicio == null || fim == null) return '';
  return `${moment(inicio).format('DD/MM/YYYY')} – ${moment(fim).format('DD/MM/YYYY')}`;
};

export const getDefaultFilter = (authDataEmail, isUsuarioUsoMutoFn, userRole, authDataId) => {
  const base = {
    inicio: moment().subtract(1, 'year').startOf('year').valueOf(),
    fim: moment().endOf('day').valueOf(),
    status: DEFAULT_STATUS,
    dateTypes: DEFAULT_DATE_TYPES,
    tipoServicos: isUsuarioUsoMutoFn(authDataEmail) ? [INSPECAO_USUO_MUTUO_ID] : null,
  };

  if (userRole === Role.DIGITADOR && authDataId) {
    base.digitadorIds = [authDataId];
  }

  if (userRole === Role.INSPETOR && authDataId) {
    base.inspetorId = [authDataId];
  }

  if (isRoleAuditoriaNsir(userRole)) {
    return { ...base, ...getNsirFiltroFixoAuditoriaNsir() };
  }
  return base;
};

/** Allowlist uso mútuo: tipo fixo nas sugestões de NSIR/equipamento e no POST do filtro. */
export const lockTipoServicoUsoMutuo = (filter) => ({
  ...(filter && typeof filter === 'object' ? filter : {}),
  tipoServicos: [INSPECAO_USUO_MUTUO_ID],
});

export const resolveNsirLocalFilter = (cacheFiltroNsir, defaultFilter, isAuditor, isUsoMutuo) => {
  let resolved = defaultFilter;
  if (cacheFiltroNsir && Object.keys(cacheFiltroNsir).length > 0) {
    resolved = isAuditor
      ? mergeFiltroNsirComRestricoesAuditoria(cacheFiltroNsir)
      : cacheFiltroNsir;
  }
  return isUsoMutuo ? lockTipoServicoUsoMutuo(resolved) : resolved;
};

export const getDefaultStatusParaFiltroNsirs = userRole =>
  (isRoleAuditoriaNsir(userRole) ? getNsirFiltroFixoAuditoriaNsir().status : DEFAULT_STATUS);

export const getStatusOptionsByRole = (userRole) => {
  if (isRoleAuditoriaNsir(userRole)) {
    return [
      { id: Status.DIGITACAO_FINALIZADA, nome: 'Digitação Finalizada' },
      { id: Status.EXECUTADA_GDIS, nome: 'Executada GDIS' },
    ];
  }

  if (userRole === Role.INSPETOR) {
    return [
      { id: Status.AGUARDANDO_INSPECAO, nome: 'Aguardando Inspeção' },
      { id: Status.EM_ANALISE, nome: 'Em Análise' },
      { id: Status.INSPECAO_EM_ENVIO, nome: 'Inspeção em Envio' },
      { id: Status.EM_INSPECAO, nome: 'Em Inspeção' },
      { id: Status.INSPECAO_ENVIADA_PARCIAL, nome: 'Inspeção Enviada Parcial' },
      { id: Status.INSPECAO_SYNC_ERROR, nome: 'Inspeção com Erro de Envio' },
      { id: Status.ERRO_AO_ENVIAR_INSPECAO, nome: 'Erro ao enviar Inspeção' },
      { id: Status.INSPECAO_FINALIZADA, nome: 'Inspeção Finalizada' },
      { id: Status.AGUARDANDO_DIGITACAO, nome: 'Aguardando Digitação' },
      { id: Status.EM_DIGITACAO, nome: 'Em Digitação' },
      { id: Status.DIGITACAO_FINALIZADA, nome: 'Digitação Finalizada' },
      { id: Status.EXECUTADA_GDIS, nome: 'Executada GDIS' },
      { id: Status.FINALIZADA, nome: 'Finalizada' },
      { id: Status.CANCELADA, nome: 'Cancelado' },
    ];
  }

  if (userRole === Role.DIGITADOR) {
    return [
      { id: Status.EM_INSPECAO, nome: 'Em Inspeção' },
      { id: Status.INSPECAO_EM_ENVIO, nome: 'Inspeção em Envio' },
      { id: Status.INSPECAO_ENVIADA_PARCIAL, nome: 'Inspeção Enviada Parcial' },
      { id: Status.INSPECAO_FINALIZADA, nome: 'Inspeção Finalizada' },
      { id: Status.EM_ANALISE, nome: 'Em Análise' },
      { id: Status.AGUARDANDO_DIGITACAO, nome: 'Aguardando Digitação' },
      { id: Status.EM_DIGITACAO, nome: 'Em Digitação' },
      { id: Status.DIGITACAO_FINALIZADA, nome: 'Digitação Finalizada' },
      { id: Status.EXECUTADA_GDIS, nome: 'Executada GDIS' },
      { id: Status.FINALIZADA, nome: 'Finalizada' },
      { id: Status.CANCELADA, nome: 'Cancelado' },
    ];
  }

  return [
    { id: Status.EM_CRIACAO, nome: 'Em Criação' },
    { id: Status.EM_ANALISE, nome: 'Em Análise' },
    { id: Status.AGUARDANDO_INSPECAO, nome: 'Aguardando Inspeção' },
    { id: Status.INSPECAO_EM_ENVIO, nome: 'Inspeção em Envio' },
    { id: Status.EM_INSPECAO, nome: 'Em Inspeção' },
    { id: Status.INSPECAO_ENVIADA_PARCIAL, nome: 'Inspeção Enviada Parcial' },
    { id: Status.INSPECAO_SYNC_ERROR, nome: 'Inspeção com Erro de Envio' },
    { id: Status.ERRO_AO_ENVIAR_INSPECAO, nome: 'Erro ao enviar Inspeção' },
    { id: Status.INSPECAO_FINALIZADA, nome: 'Inspeção Finalizada' },
    { id: Status.AGUARDANDO_DIGITACAO, nome: 'Aguardando Digitação' },
    { id: Status.EM_DIGITACAO, nome: 'Em Digitação' },
    { id: Status.DIGITACAO_FINALIZADA, nome: 'Digitação Finalizada' },
    { id: Status.EXECUTADA_GDIS, nome: 'Executada GDIS' },
    { id: Status.FINALIZADA, nome: 'Finalizada' },
    { id: Status.CANCELADA, nome: 'Cancelado' },
  ];
};

export const DIALOG_TYPES = {
  IMPORT: 'import',
  IMPORT_NSMPS: 'importNsmps',
  IMPORT_FINANCEIRO: 'importFinanceiro',
  IMPORT_DIGITADOR: 'importDigitador',
  EXPORT: 'export',
  EXPORT_EDIT: 'exportEdit',
  EXPORT_SERVICO: 'exportServico',
  EXPORT_FINANCEIRO: 'exportFinanceiro',
  EXPORT_DIGITADOR: 'exportDigitador',
  EXPORT_INSPECAO: 'exportInspecao',
  EXPORT_REPORT_USO_MUTUO: 'exportReportUsoMutuo',
  EXPORT_DIGITADOR_DIARIO: 'exportDigitadorDiario',
  EXPORT_PROJETO_OBRAS: 'exportProjetoObras',
  IMPORT_PREVIEW: 'importPreview',
};

export const ERROR_MESSAGES = {
  INVALID_SHEET: "Planilha inválida. Por favor, utilize a planilha 'Planilha Importação' que se encontra na aba de Documentos!",
  INVALID_FILE: "Arquivo inválido. Por favor, utilize um arquivo válido para importar os serviços!",
  INVALID_DIGITADOR_SHEET: "Planilha inválida. Por favor, exporte a planilha de digitadores e utilize-a!",
  READ_ERROR: 'Erro ao abrir a planilha. Por favor, tente novamente!',
};

export const EXPORT_MESSAGES = {
  NSIRS: 'Deseja exportar todas as NSIRs filtradas?',
  EDIT: 'Deseja exportar todas as NSIRs filtradas para edição?',
  SERVICO: 'Selecione o escopo e confirme a exportação dos serviços para as NSIRs filtradas.',
  SERVICO_BY_ID: 'Selecione o escopo e confirme a exportação dos serviços desta NSIR.',
  INSPECAO: 'Deseja exportar as informações da inspeção para as NSIRs filtradas?',
  FINANCEIRO: 'Selecione o formato e confirme a exportação das NSIRs filtradas para financeiro.',
  DIGITADOR: 'Deseja exportar todas as NSIRs filtradas para digitadores?',
  USO_MUTUO: 'Deseja exportar todas as NSIRs filtradas para Uso Mútuo?',
  DIGITADOR_DIARIO: 'Deseja exportar o relatório de digitadores diários para os últimos 7 dias?',
  PROJETO_OBRAS: 'Deseja exportar o relatório de Projeto e Obras para as NSIRs filtradas?',
};

export const EXPORT_FINANCEIRO_FORMAT = {
  SINGLE_TAB: false,
  SPLIT_BY_INSPETOR: true,
};

export const EXPORT_FINANCEIRO_FORMAT_LABELS = {
  [false]: 'Todas NSIRs em uma única aba',
  [true]: 'Resumo e abas por inspetor',
};

export const EXPORT_SERVICO_APENAS_DIGITADOS = {
  APENAS_DIGITADOS: true,
  TODOS_INSPECIONADOS: false,
};

export const EXPORT_SERVICO_APENAS_DIGITADOS_LABELS = {
  [true]: 'Apenas serviços digitados',
  [false]: 'Todos os serviços inspecionados (digitados ou não)',
};

export const DIGITADOR_DIARIO_ALLOWED_EMAILS = [
  'vinicius_admin',
  'NUBIA CRISTINA RIGUEIRA SALGADO',
  'RAFAEL DIAS GARCIA',
];

export const CARGA_DRILL_STATUSES = [
  Status.AGUARDANDO_DIGITACAO,
  Status.EM_DIGITACAO,
];

export const parseCargaQuery = (search) => {
  const params = new URLSearchParams(search || '');
  return {
    malhaId: params.get('malhaId') || params.get('gerenciaId') || null,
    digitadorId: params.get('digitadorId') || null,
  };
};

export const hasCargaQuery = ({ malhaId, digitadorId } = {}) =>
  Boolean(malhaId || digitadorId);

export const applyCargaQueryToFilter = (baseFilter, { malhaId, digitadorId } = {}) => {
  if (!malhaId && !digitadorId) return baseFilter;
  return {
    inicio: baseFilter?.inicio,
    fim: baseFilter?.fim,
    dateTypes: baseFilter?.dateTypes,
    tipoServicos: baseFilter?.tipoServicos ?? null,
    malhaId: malhaId ? [malhaId] : null,
    gerenciaId: null,
    poloId: null,
    subestacaoId: null,
    alimentadorId: null,
    digitadorId: digitadorId ? [digitadorId] : [],
    digitadorIds: digitadorId ? [digitadorId] : [],
    status: CARGA_DRILL_STATUSES,
  };
};

const toArray = (v) => (Array.isArray(v) ? v : [v]);

const hasNonEmptyArray = (v) => Array.isArray(v) && v.length > 0;

const isPresentScalar = (v) => v != null && v !== '';

/** Chaves do collapse “Mais filtros” — badge no botão Filtros. */
const ADVANCED_FILTER_KEYS = [
  'possuiCroqui',
  'possuiHd',
  'possuiApro',
  'apros',
  'apro',
  'hds',
  'hd',
  'prioridades',
  'prioridade',
  'clientes',
  'digitacaoAntecipada',
  'ctPlans',
  'ctPlan',
  'ctExecs',
  'ctExec',
  'nsirPagaInspetor',
];

export const countNsirAdvancedFilters = (filter) => {
  if (!filter || typeof filter !== 'object') return 0;
  return ADVANCED_FILTER_KEYS.reduce((acc, key) => {
    const value = filter[key];
    if (hasNonEmptyArray(value) || isPresentScalar(value)) return acc + 1;
    return acc;
  }, 0);
};

/** Rótulos curtos dos filtros avançados ativos (para resumo no collapse). */
export const summarizeNsirAdvancedFilters = (filter) => {
  if (!filter || typeof filter !== 'object') return [];

  const items = [];
  const pushFlag = (key, label) => {
    if (isPresentScalar(filter[key])) items.push(label);
  };
  const pushMulti = (arrKey, scalarKey, label) => {
    if (hasNonEmptyArray(filter[arrKey])) {
      items.push(`${label} (${filter[arrKey].length})`);
      return;
    }
    if (isPresentScalar(filter[scalarKey])) items.push(label);
  };

  pushFlag('possuiCroqui', 'Possui Croqui');
  pushFlag('possuiHd', 'Possui HD');
  pushFlag('possuiApro', 'Possui APRO');
  pushMulti('apros', 'apro', 'APRO');
  pushMulti('hds', 'hd', 'HD');
  pushMulti('prioridades', 'prioridade', 'Prioridade');
  if (hasNonEmptyArray(filter.clientes)) {
    items.push(`Clientes (${filter.clientes.length})`);
  }
  pushFlag('digitacaoAntecipada', 'Dig. antecipada');
  pushMulti('ctPlans', 'ctPlan', 'CT Plan');
  pushMulti('ctExecs', 'ctExec', 'CT Exec');
  pushFlag('nsirPagaInspetor', 'Paga inspetor');

  return items;
};

export const normalizeNsirFilterForApi = (filter) => {
  if (!filter || typeof filter !== 'object') return {};

  const {
    inspetorId,
    digitadorId,
    inspetorIds,
    digitadorIds,
    codigo,
    equipamento,
    equipamentos,
    numNsmp,
    numNsmps,
    ctPlan,
    ctPlans,
    ctExec,
    ctExecs,
    apro,
    apros,
    hd,
    hds,
    prioridade,
    prioridades,
    ...rest
  } = filter;
  const body = { ...rest };

  if (hasNonEmptyArray(inspetorIds)) {
    body.inspetorIds = inspetorIds;
  } else if (inspetorId != null && inspetorId !== '') {
    body.inspetorIds = toArray(inspetorId);
  }

  if (hasNonEmptyArray(digitadorIds)) {
    body.digitadorIds = digitadorIds;
  } else if (digitadorId != null && digitadorId !== '') {
    body.digitadorIds = toArray(digitadorId);
  }

  if (hasNonEmptyArray(filter.nsirIds)) {
    body.nsirIds = filter.nsirIds;
    delete body.codigo;
  } else if (isPresentScalar(codigo)) {
    body.codigo = codigo;
  }

  if (hasNonEmptyArray(equipamentos)) {
    body.equipamentos = equipamentos;
  } else if (isPresentScalar(equipamento)) {
    body.equipamento = equipamento;
  }

  if (hasNonEmptyArray(numNsmps)) {
    body.numNsmps = numNsmps;
  } else if (isPresentScalar(numNsmp)) {
    body.numNsmp = numNsmp;
  }

  if (hasNonEmptyArray(ctPlans)) {
    body.ctPlans = ctPlans;
  } else if (isPresentScalar(ctPlan)) {
    body.ctPlan = ctPlan;
  }

  if (hasNonEmptyArray(ctExecs)) {
    body.ctExecs = ctExecs;
  } else if (isPresentScalar(ctExec)) {
    body.ctExec = ctExec;
  }

  if (hasNonEmptyArray(apros)) {
    body.apros = apros.map((v) => (typeof v === 'number' ? v : Number(v))).filter((n) => !Number.isNaN(n));
  } else if (apro != null && apro !== '') {
    body.apro = apro;
  }

  if (hasNonEmptyArray(hds)) {
    body.hds = hds.map((v) => (typeof v === 'number' ? v : Number(v))).filter((n) => !Number.isNaN(n));
  } else if (hd != null && hd !== '') {
    body.hd = hd;
  }

  if (hasNonEmptyArray(prioridades)) {
    body.prioridades = prioridades;
  } else if (isPresentScalar(prioridade)) {
    body.prioridade = prioridade;
  }

  const applyGeoIds = (singularKey, pluralKey) => {
    const raw = filter[pluralKey] != null ? filter[pluralKey] : filter[singularKey];
    delete body[singularKey];
    delete body[pluralKey];
    if (hasNonEmptyArray(raw)) {
      body[pluralKey] = raw;
      return;
    }
    if (isPresentScalar(raw)) {
      body[pluralKey] = [raw];
    }
  };

  applyGeoIds('malhaId', 'malhaIds');
  applyGeoIds('gerenciaId', 'gerenciaIds');
  applyGeoIds('poloId', 'poloIds');
  applyGeoIds('subestacaoId', 'subestacaoIds');
  applyGeoIds('alimentadorId', 'alimentadorIds');

  if (filter.vencimentoUrgency) {
    body.vencimentoUrgency = filter.vencimentoUrgency;
  }

  const digitacaoAntecipada = toDigitacaoAntecipadaApiFlag(filter.digitacaoAntecipada);
  delete body.digitacaoAntecipada;
  if (digitacaoAntecipada !== undefined) {
    body.digitacaoAntecipada = digitacaoAntecipada;
  }

  return body;
};
